import {useEffect, useState} from 'react'

import {config} from '../config'

export type Theme = 'light' | 'dark'

function isSystemThemeEnabled(): boolean {
  return config.apiMode === 'auth'
}

function readSystemTheme(): Theme {
  if (typeof window === 'undefined') {
    return config.theme ?? 'light'
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveClientTheme(): Theme {
  if (typeof window === 'undefined') {
    return config.theme ?? 'light'
  }

  // Темная тема только для auth режима
  if (config.apiMode !== 'auth') {
    return 'light'
  }

  const params = new URLSearchParams(window.location.search)
  const queryTheme = params.get('theme')

  if (queryTheme === 'dark' || queryTheme === 'light') {
    return queryTheme
  }

  if (isSystemThemeEnabled()) {
    return readSystemTheme()
  }

  return config.theme ?? 'light'
}

/**
 * Хук для определения текущей темы приложения.
 *
 * Логика определения:
 * 1. Initial state: из config.theme (SSR) с client-side reconcile
 * 2. Client-side: подписка на system preference (только для auth режима)
 * 3. Синхронизация data-theme атрибута на <html>
 */
export function useTheme(): Theme {
  const [theme, setTheme] = useState<Theme>(() => resolveClientTheme())

  useEffect(() => {
    if (typeof window === 'undefined') return

    document.documentElement.setAttribute('data-theme', theme)

    const params = new URLSearchParams(window.location.search)

    if (params.has('theme') || !isSystemThemeEnabled()) {
      return
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const listener = (e: MediaQueryListEvent) => {
      setTheme(e.matches ? 'dark' : 'light')
    }

    mediaQuery.addEventListener('change', listener)
    return () => mediaQuery.removeEventListener('change', listener)
  }, [theme])

  return theme
}
